import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInCalendarDays, format } from 'date-fns';

import { useDispatch, useSelector } from 'react-redux';

import { saveJob, fetchSavedJobs, deleteSavedJob } from '../../../redux/SavedJobSlice/savedJobSlice';
import { fetchReferralScore, saveRecruiterContacts, fetchCachedRecruiters, setCompanyLoading } from '../../../redux/ContactSlice/contactSlice';
import { fetchRecruiterForJob } from '../../../redux/JobSlice/jobSlice';

import { Bookmark, Users, Share2, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';

import RecruiterModal from './RecruiterModal';
import JobDetailsModal from './JobDetailsModal';
import JobDetailPage from '../../../pages/NavPages/JobPage/JobDetailPage';
import api from '../../../api/axios';

import { DollarSign, Heart, TrendingUp, AlertTriangle, Shield } from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { normalizeRecruiterSearchMessage } from '../../../utils/userFacingMessages';

const ScoreDisplay = ({ status, score, category = null }) => {
    if (status === 'failed') {
        return <span className="text-gray-400 text-sm font-medium" title="Score calculation failed">N/A</span>;
    }
    if (status === 'processing') {
        return <span className="text-blue-600 text-sm font-medium" title="Score is being calculated">Processing</span>;
    }
    if (status === 'pending') {
        return <span className="text-slate-400 text-sm font-medium animate-pulse" title="Score calculation queued">Calculating…</span>;
    }
    if (status === null || score == null) {
        return <span className="text-gray-400 text-sm font-medium" title="Score not available">Not rated</span>;
    }

    const getScoreLabel = (score, category = null) => {
        // Without this a missing score fell past every threshold to "Limited" —
        // the worst rating on the scale — so a company we never researched was
        // shown in red as though we had found something damning.
        if (score == null || !Number.isFinite(Number(score))) {
            return { text: 'No data', color: 'text-gray-400' };
        }
        if (category === 'toxicity') {
            if (score >= 80) return { text: 'No Toxicity Signals Identified', color: 'text-emerald-600' };
            if (score >= 60) return { text: 'Low Toxicity', color: 'text-green-600' };
            if (score >= 40) return { text: 'Average', color: 'text-blue-600' };
            if (score >= 25) return { text: 'Possibly Toxic', color: 'text-amber-600' };
            return { text: 'High Toxicity Signals Identified', color: 'text-rose-600' };
        }

        if (score >= 85) return { text: 'Excellent', color: 'text-emerald-600' };
        if (score >= 70) return { text: 'Very Good', color: 'text-green-600' };
        if (score >= 55) return { text: 'Good', color: 'text-blue-600' };
        if (score >= 40) return { text: 'Moderate', color: 'text-amber-600' };
        if (score >= 25) return { text: 'Fair', color: 'text-orange-600' };
        return { text: 'Limited', color: 'text-rose-600' };
    };

    const { text, color } = getScoreLabel(score, category);

    return (
        <span className={`${color} text-sm font-semibold`}>
            {text}
        </span>
    );
};

// Helper: get score badge color (dynamic, inline style)
const getScoreBadgeColor = (score) => {
    if (score === null || score === undefined) return '#94A3B8';
    if (score >= 85) return '#10B981';
    if (score >= 70) return '#3F57E0';
    if (score >= 55) return '#F59E0B';
    if (score >= 40) return '#F97316';
    return '#94A3B8';
};

// Helper: get circle bg/border color based on score value
const getCircleColors = (score, category = null) => {
    if (score == null || !Number.isFinite(Number(score))) {
        return { bg: '#F8FAFC', border: '#E2E8F0', text: '#94A3B8' };
    }
    const s = Number(score);
    if (category === 'toxicity') {
        if (s >= 80) return { bg: '#F0FDF4', border: '#86EFAC', text: '#166534' };
        if (s >= 60) return { bg: '#F0FDF4', border: '#86EFAC', text: '#166534' };
        if (s >= 40) return { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' };
        if (s >= 25) return { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' };
        return { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B' };
    }
    if (s >= 85) return { bg: '#F0FDF4', border: '#86EFAC', text: '#166534' };
    if (s >= 70) return { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' };
    if (s >= 55) return { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' };
    if (s >= 40) return { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E' };
    return { bg: '#FFF7ED', border: '#FDBA74', text: '#9A3412' };
};

// Reason code → ok flag
const reasonCodeIsOk = (code) => {
    const BAD_CODES = ['CLEARANCE_REQUIRED', 'COMPETITION_HIGH', 'SALARY_UNKNOWN'];
    return !BAD_CODES.includes(code);
};

const JobCard = ({ job, jobId, jobFitScore, referral, postedAt, handleCardClick, savedJobs, selectedJobs, setSelectedJobs, setToastMessage, handleSaveSelectedJobs, loadingJobId, setLoadingJobId, setLoadingCompanyName, onAddCompanyCard, onUpdateCompanyAnalysis, isNew = false }) => {

    const getCompanyName = (job) => {
        return job.companyId?.name || job.companyName || 'Unknown Company';
    };

    const getCompanyLogo = (job) => {
        const existing = job.companyId?.companyLogo || job.companyLogo;
        if (existing) return existing;
        // Derive a logo from the company domain for sources that don't carry one (e.g. ATS
        // jobs), via DuckDuckGo's icon service. If the guessed domain has no icon, the
        // <img onError> falls back to the generic icon.
        const slug = String(job.companyName || job.companyId?.name || job.company || '')
            .trim().toLowerCase().replace(/[^a-z0-9]/g, '');
        if (slug) return `https://icons.duckduckgo.com/ip3/${slug}.com.ico`;
        return 'https://cdn-icons-png.flaticon.com/512/2721/2721294.png';
    };

    const cardRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const normalizedScoresStatus = job.scoresStatus ?? job.companyId?.scoresStatus ?? null;

    const hasReduxCompanyScores = job.companyScores && Object.values(job.companyScores).some((value) => Number.isFinite(Number(value)) && value > 0);

    const normalizedCompanyScores = hasReduxCompanyScores ? job.companyScores : (job.companyId?.scores && typeof job.companyId.scores === 'object' ? {
        compensation: job.companyId.scores.compensation?.weightedScore,
        stability: job.companyId.scores.stability?.weightedScore,
        growth: job.companyId.scores.careerGrowth?.weightedScore,
        WLB: job.companyId.scores.workLifeBalance?.weightedScore,
        toxicity: job.companyId.scores.toxicity?.weightedScore,
        referral: job.companyId.scores.referral?.weightedScore,
    } : {});

    const rankedScore = typeof job.score === 'number' ? Math.round(job.score) : null;
    const matchLabel = typeof job.matchLabel === 'string' ? job.matchLabel : null;
    const competitionLabel = typeof job.competitionLabel === 'string' ? job.competitionLabel : null;
    const rankedReasonCodes = Array.isArray(job.reasonCodes) ? job.reasonCodes : [];
    const rawReasons = Array.isArray(job.reasons)
        ? job.reasons
        : (typeof job.reasons === 'string' && job.reasons.trim() ? [job.reasons.trim()] : []);
    const rankedReasons = rawReasons
        .slice(0, 5)
        .map((reason, index) => {
            if (reason && typeof reason === 'object') {
                return {
                    text: String(reason.text || reason.reason || '').trim(),
                    code: typeof reason.code === 'string' ? reason.code : (typeof rankedReasonCodes[index] === 'string' ? rankedReasonCodes[index] : 'GENERAL_ALIGNMENT'),
                    priority: Number.isFinite(Number(reason.priority)) ? Number(reason.priority) : 3
                };
            }
            return {
                text: String(reason || '').trim(),
                code: typeof rankedReasonCodes[index] === 'string' ? rankedReasonCodes[index] : 'GENERAL_ALIGNMENT',
                priority: 3
            };
        }).filter((reason) => reason.text);

    const rankedEndpointAvailable = job?.rankingMeta?.rankedEndpointAvailable !== false;
    const hasRankedMetadata = Boolean(job?.rankingMeta?.hasRankedData) || rankedScore !== null || matchLabel !== null || rankedReasons.length > 0;

    const normalizedMatchLabel = matchLabel === 'Strong match'
        ? 'Strong fit'
        : matchLabel === 'Good match'
            ? 'Good fit'
            : matchLabel === 'Fair match'
                ? 'Possible fit'
                : matchLabel === 'Low match'
                    ? 'Possible fit'
                    : matchLabel;

    const user = useSelector((state) => state.auth.user);

    const [isExpanded, setIsExpanded] = useState(false);
    const [referralLoading, setReferralLoading] = useState(false);
    const [recruiterLoading, setRecruiterLoading] = useState(false);
    const [isRecruiterModalOpen, setIsRecruiterModalOpen] = useState(false);
    const [recruiterInfo, setRecruiterInfo] = useState(null);
    const [showFetchConfirmation, setShowFetchConfirmation] = useState(false);
    const [pendingJobData, setPendingJobData] = useState(null);
    const [isJobDetailsModalOpen, setIsJobDetailsModalOpen] = useState(false);
    // Heavy detail fields (jobDesc, descriptionSections) are NOT in the list payload —
    // fetch the full job on demand when a detail/recruiter modal is opened.
    const [detailJob, setDetailJob] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const ensureFullJob = async () => {
        if (detailJob || detailLoading || !job?._id) return;
        setDetailLoading(true);
        try {
            const { data } = await api.get(`/api/jobs/job/${job._id}`);
            if (data?.job) setDetailJob({ ...job, ...data.job });
        } catch (e) {
            console.error('Failed to load full job:', e?.message);
        } finally {
            setDetailLoading(false);
        }
    };
    // Fetch the full job whenever a detail/recruiter modal opens (covers every trigger).
    useEffect(() => {
        if (isJobDetailsModalOpen || isRecruiterModalOpen) ensureFullJob();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isJobDetailsModalOpen, isRecruiterModalOpen]);
    const [showRecruiterOverlay, setShowRecruiterOverlay] = useState(false);
    const [isFromDatabase, setIsFromDatabase] = useState(false);
    const [savedToast, setSavedToast] = useState(false);

    // NEW: state to track if user has clicked "Show Referral Path"
    const [referralPathRequested, setReferralPathRequested] = useState(false);
    // Inline recruiter results for expanded card
    const [inlineRecruiterData, setInlineRecruiterData] = useState(null);
    const [inlineRecruiterLoading, setInlineRecruiterLoading] = useState(false);

    useEffect(() => {
        return () => {
            requestInProgressRef.current = false;
        };
    }, []);

    const isSelected = selectedJobs.some(j => j._id === jobId);
    const isAlreadySaved = savedJobs.some(j => j.jobId?._id === jobId);

    const getPostedDate = (datentime) => {
        if (!datentime) return null;
        if (datentime.$date?.$numberLong) {
            return new Date(parseInt(datentime.$date.$numberLong, 10));
        }
        if (typeof datentime === "string" || typeof datentime === "number") {
            return new Date(datentime);
        }
        return null;
    };

    const postedTimeAgo = (() => {
        const date = getPostedDate(job.datentime || job.postedDate || job.postedAt);
        if (!date) return 'Posted date unavailable';
        // Hour-level granularity for fresh jobs, so a "posted within 3h" fetch
        // shows how fresh each job actually is instead of a flat "today".
        const hoursOld = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
        if (hoursOld >= 0 && hoursOld < 1) return 'Posted just now';
        if (hoursOld >= 1 && hoursOld < 24) return `Posted ${hoursOld}h ago`;
        const dayDelta = differenceInCalendarDays(new Date(), date);
        if (dayDelta <= 0) return 'Posted today';
        if (dayDelta === 1) return 'Posted yesterday';
        if (dayDelta <= 7) return `Posted ${dayDelta} days ago`;
        if (dayDelta <= 30) {
            const weeks = Math.max(1, Math.round(dayDelta / 7));
            return `Posted ${weeks} week${weeks === 1 ? '' : 's'} ago`;
        }
        return `Posted on ${format(date, 'MMM d, yyyy')}`;
    })();

    const toggleExpand = () => {
        setIsExpanded((prev) => !prev);
    };

    const handleCheckboxChange = (e) => {
        const checked = e.target.checked;
        if (!isAlreadySaved) {
            if (checked) {
                setSelectedJobs((prev) => [...prev, job]);
            } else {
                setSelectedJobs((prev) => prev.filter((j) => j._id !== jobId));
            }
        }
    };

    const handleReferralPossibility = async (job) => {
        setReferralLoading(true);
        try {
            const payload = {
                companyName: getCompanyName(job),
                preferredDepartment: user.preferredDepartment || []
            };
            await dispatch(fetchReferralScore(payload)).unwrap();
        } catch (error) {
            console.error("Failed to fetch referral contacts:", error);
            alert(`Error: ${error.message || error}`);
        } finally {
            setReferralLoading(false);
        }
    };

    const requestInProgressRef = useRef(false);

    const handleFindRecruiter = async () => {
        console.log('[JobCard] Show referral path clicked for:', getCompanyName(job));

        if (recruiterLoading || requestInProgressRef.current) {
            console.log('[JobCard] Recruiter request already in progress, ignoring click');
            return;
        }

        requestInProgressRef.current = true;
        console.log('[JobCard] Setting loading state for job:', jobId);
        setLoadingJobId(jobId);
        setLoadingCompanyName(getCompanyName(job));

        // Set inline loading state
        setReferralPathRequested(true);
        setInlineRecruiterLoading(true);

        const matchingSavedJob = savedJobs.find(s => s.jobId?._id === job._id);
        const organizationId = matchingSavedJob?.organizationId || job.organizationId || job.companyId;
        if (organizationId) {
            dispatch(setCompanyLoading({ organizationId }));
        }

        setSelectedJobs((prev) => {
            const isAlreadySelected = prev.some(j => j._id === job._id);
            if (!isAlreadySelected && !isAlreadySaved) {
                return [...prev, job];
            }
            return prev;
        });

        setRecruiterLoading(true);

        setToastMessage({
            show: true,
            message: `Searching for recruiters at ${getCompanyName(job)}...`,
            type: 'info'
        });

        try {
            let recruiterList = null;
            let fetchedFromDB = false;

            if (isAlreadySaved) {
                const savedJobData = savedJobs.find(j => j.jobId?._id === jobId);
                const organizationId = savedJobData?.organizationId;

                if (organizationId) {
                    console.log('[JobCard] Checking database for cached recruiters for org:', organizationId);

                    if (onAddCompanyCard) {
                        onAddCompanyCard(job, null);
                    }

                    try {
                        console.log('[JobCard] Dispatching fetchCachedRecruiters...');
                        setIsFromDatabase(true);
                        recruiterList = await dispatch(fetchCachedRecruiters({ organizationId })).unwrap();

                        const totalCached = (recruiterList?.hiring_manager?.best_guesses?.length || 0) +
                                          (recruiterList?.recruiters?.best_guesses?.length || 0);

                        if (totalCached > 0) {
                            fetchedFromDB = true;
                            console.log(`[JobCard] Using ${totalCached} cached recruiters from database`);
                        } else {
                            console.log('[JobCard] No recruiters in database, will fetch from API');
                            setIsFromDatabase(false);
                        }
                    } catch (error) {
                        console.log('[JobCard] Database fetch failed, will fetch from API:', error.message);
                        setIsFromDatabase(false);
                    }
                }
            }

            if (!fetchedFromDB) {
                console.log('[JobCard] Recruiters not in database. Will fetch from API');

                if (onAddCompanyCard) {
                    onAddCompanyCard(job, null);
                }

                setPendingJobData(job);

                console.log('[JobCard] Fetching recruiter data from API for:', getCompanyName(job));
                recruiterList = await dispatch(fetchRecruiterForJob({ jobDetails: job })).unwrap();

                console.log('[JobCard] Received recruiter data from API:', recruiterList);

                if (isAlreadySaved && recruiterList) {
                    const savedJobData = savedJobs.find(j => j.jobId?._id === jobId);
                    const organizationId = savedJobData?.organizationId;
                    const companyId = savedJobData?.companyId;

                    if (organizationId && (recruiterList.hiring_manager?.best_guesses?.length > 0 || recruiterList.recruiters?.best_guesses?.length > 0)) {
                        try {
                            await dispatch(saveRecruiterContacts({
                                jobId,
                                companyId,
                                organizationId,
                                recruiterData: recruiterList
                            })).unwrap();
                        } catch (saveError) {
                            console.error('[JobCard] Failed to save recruiters to DB, but continuing:', saveError);
                        }
                    }
                }
            }

            if (requestInProgressRef.current) {
                if (recruiterList && (recruiterList.hiring_manager?.best_guesses?.length > 0 || recruiterList.recruiters?.best_guesses?.length > 0)) {
                    const hiringManagerCount = recruiterList.hiring_manager?.best_guesses?.length || 0;
                    const recruiterCount = recruiterList.recruiters?.best_guesses?.length || 0;
                    const totalCount = hiringManagerCount + recruiterCount;

                    const sourceMessage = fetchedFromDB ? 'loaded from your saved contacts' : 'found & saved to your Networking tab';
                    console.log(`[JobCard] ${totalCount} recruiter${totalCount !== 1 ? 's' : ''} ${sourceMessage}`);

                    // Set inline recruiter data
                    setInlineRecruiterData(recruiterList);

                    if (onAddCompanyCard) {
                        onAddCompanyCard(job, recruiterList);
                    }
                } else {
                    console.warn('[JobCard] No recruiters found in response');
                    setInlineRecruiterData({ fetchFailed: true });
                    if (onAddCompanyCard) {
                        onAddCompanyCard(job, {
                            fetchFailed: true,
                            errorMessage: normalizeRecruiterSearchMessage('No verified recruiter contacts are available for this company yet.')
                        });
                    }
                    setToastMessage({
                        show: true,
                        message: normalizeRecruiterSearchMessage('No verified recruiter contacts are available for this company yet.'),
                        type: 'error'
                    });
                    setLoadingJobId(null);
                }
            }
        } catch (error) {
            if (requestInProgressRef.current) {
                setSelectedJobs((prev) => prev.filter((j) => j._id !== job._id));
                console.error("[JobCard] Failed to fetch recruiter contacts:", error);

                const displayMessage = normalizeRecruiterSearchMessage(error);

                setToastMessage({
                    show: true,
                    message: displayMessage,
                    type: 'error'
                });
                setInlineRecruiterData({ fetchFailed: true, errorMessage: displayMessage });
                if (onAddCompanyCard) {
                    onAddCompanyCard(job, {
                        fetchFailed: true,
                        errorMessage: displayMessage
                    });
                }
                setLoadingJobId(null);
            }
        } finally {
            setRecruiterLoading(false);
            setInlineRecruiterLoading(false);
            requestInProgressRef.current = false;
        }
    };

    const handleCloseRecruiterModal = () => {
        setIsRecruiterModalOpen(false);
        setSelectedJobs((prev) => prev.filter((j) => j._id !== job._id));
        requestInProgressRef.current = false;
    };

    const handleConfirmFetch = async () => {
        setShowFetchConfirmation(false);

        if (!pendingJobData) return;

        setRecruiterLoading(true);
        setShowRecruiterOverlay(true);
        setIsFromDatabase(false);
        requestInProgressRef.current = true;

        const matchingSavedJob = savedJobs.find(s => s.jobId?._id === pendingJobData._id);
        const organizationId = matchingSavedJob?.organizationId || pendingJobData.organizationId || pendingJobData.companyId;
        if (organizationId) {
            dispatch(setCompanyLoading({ organizationId }));
        }

        try {
            console.log('[JobCard] User confirmed - fetching recruiter data from API for:', pendingJobData.companyName);
            const recruiterList = await dispatch(fetchRecruiterForJob({ jobDetails: pendingJobData })).unwrap();

            if (isAlreadySaved && recruiterList) {
                const savedJobData = savedJobs.find(j => j.jobId?._id === jobId);
                const organizationId = savedJobData?.organizationId;
                const companyId = savedJobData?.companyId;

                if (organizationId && (recruiterList.hiring_manager?.best_guesses?.length > 0 || recruiterList.recruiters?.best_guesses?.length > 0)) {
                    try {
                        await dispatch(saveRecruiterContacts({
                            jobId,
                            companyId,
                            organizationId,
                            recruiterData: recruiterList
                        })).unwrap();
                    } catch (saveError) {
                        console.error('[JobCard] Failed to save recruiters to DB, but continuing:', saveError);
                    }
                }
            }

            if (recruiterList && (recruiterList.hiring_manager?.best_guesses?.length > 0 || recruiterList.recruiters?.best_guesses?.length > 0)) {
                const hiringManagerCount = recruiterList.hiring_manager?.best_guesses?.length || 0;
                const recruiterCount = recruiterList.recruiters?.best_guesses?.length || 0;
                const totalCount = hiringManagerCount + recruiterCount;

                console.log(`[JobCard] ${totalCount} recruiter${totalCount !== 1 ? 's' : ''} found`);

                setInlineRecruiterData(recruiterList);

                if (onAddCompanyCard) {
                    onAddCompanyCard(pendingJobData, recruiterList);
                }
            } else {
                setToastMessage({
                    show: true,
                    message: normalizeRecruiterSearchMessage('No verified recruiter contacts are available for this company yet.'),
                    type: "error"
                });
                setInlineRecruiterData({ fetchFailed: true });
            }
        } catch (error) {
            console.error("Failed to fetch recruiter contacts:", error);
            const displayMessage = normalizeRecruiterSearchMessage(error);

            setToastMessage({
                show: true,
                message: displayMessage,
                type: 'error'
            });
            setInlineRecruiterData({ fetchFailed: true, errorMessage: displayMessage });
        } finally {
            setRecruiterLoading(false);
            setShowRecruiterOverlay(false);
            requestInProgressRef.current = false;
            setPendingJobData(null);
        }
    };

    const savedJobEntry = savedJobs.find((j) => j.jobId?._id === jobId) || null;

    const sourceValue = String(job.source || '').toLowerCase();
    const sourceMeta = sourceValue === 'ats'
        ? { dot: 'bg-[#1246D6]', text: 'Early access' }
        : sourceValue === 'rapidapi'
            ? { dot: 'bg-[#94a3b8]', text: 'Job board' }
            : null;
    const workType = job.workType || job.jobType || 'Not specified';
    const personalizationStatus = job.personalizationStatus || (hasRankedMetadata ? 'success' : 'pending');

    const fallbackReason = hasRankedMetadata
        ? 'We are still refining this match against your profile preferences.'
        : personalizationStatus === 'pending'
            ? 'Scoring is based on your onboarding profile (target role, industry, department, and salary), and detailed reasons are loading for this card.'
            : personalizationStatus === 'failed'
                ? 'Personalized matching is temporarily unavailable.'
                : 'Scoring is based on your onboarding profile (target role, industry, department, and salary), and detailed reasons are loading for this card.';

    const visibleFitReasons = (rankedReasons.length > 0
        ? rankedReasons.slice(0, 4)
        : [{ text: fallbackReason, code: 'GENERAL_ALIGNMENT', priority: 3 }]);

    const hasMatchLabel = typeof normalizedMatchLabel === 'string' && normalizedMatchLabel.trim().length > 0;
    const isReviewingFit = personalizationStatus === 'pending' || personalizationStatus === 'processing';
    // Mirrors backend getMatchLabel thresholds — low scores show honestly as
    // "Weak fit" instead of being dressed up as "Possible fit".
    const fallbackFitFromScore = rankedScore === null
        ? null
        : rankedScore >= 70
            ? 'Strong fit'
            : rankedScore >= 50
                ? 'Good fit'
                : rankedScore >= 35
                    ? 'Possible fit'
                    : 'Weak fit';
    const fitBadgeLabel = isReviewingFit
        ? 'Reviewing fit'
        : (rankedScore === null
            ? null
            : (hasMatchLabel ? normalizedMatchLabel : fallbackFitFromScore));
    const fitBadgeClass = fitBadgeLabel === 'Strong fit'
        ? 'bg-[#f0fdf4] text-[#166534] border-[#bbf7d0]'
        : fitBadgeLabel === 'Good fit'
            ? 'bg-[#eff6ff] text-[#1e40af] border-[#bfdbfe]'
            : fitBadgeLabel === 'Possible fit'
                ? 'bg-[#f8fafc] text-[#64748b] border-[#e2e8f0]'
                : 'bg-[#f8fafc] text-[#94a3b8] border-[#e2e8f0]';
    const showFitIcon = fitBadgeLabel === 'Strong fit' || fitBadgeLabel === 'Good fit';

    const companyScoreValues = [
        normalizedCompanyScores?.compensation,
        normalizedCompanyScores?.WLB,
        normalizedCompanyScores?.growth,
        normalizedCompanyScores?.toxicity,
        normalizedCompanyScores?.stability,
        normalizedCompanyScores?.referral
    ];
    const hasCompanyScores = companyScoreValues.some((value) => Number.isFinite(Number(value)));
    const scoresProcessing = !hasCompanyScores && normalizedScoresStatus === 'processing';
    // Always shown. Hiding the section for an unresearched company left a gap in
    // the card and implied the scores were still loading; the indicators stay and
    // each reads "No data".
    const showScoresSection = true;

    const scoreAccent = rankedScore === null ? '#888780' : (rankedScore >= 70 ? '#1246D6' : rankedScore >= 50 ? '#378ADD' : '#888780');
    const scoreBreakdown = job.scoreBreakdown || null;
    const qualityFlags = job?.qualityFlags && typeof job.qualityFlags === 'object' ? job.qualityFlags : null;
    const qualityIssueCount = qualityFlags
        ? ['titleMissing', 'salaryMissing', 'applyCountMalformed', 'postedDateMissing']
            .reduce((count, key) => count + (qualityFlags[key] ? 1 : 0), 0)
        : 0;

    const userSignals = { preferredDepartment: user?.preferredDepartment || [] };
    const buildMeaningSentence = (jobData, signals) => {
        const title = jobData.jobPosition || jobData.title || 'This role';
        const preferredDept = Array.isArray(signals?.preferredDepartment) && signals.preferredDepartment[0]
            ? signals.preferredDepartment[0]
            : null;
        const compensationFloor = Number(signals?.compensationMin);
        const compensationText = Number.isFinite(compensationFloor) && compensationFloor > 0
            ? compensationFloor.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
            : 'your compensation preference';
        if (preferredDept) {
            return `${title} lines up with your ${preferredDept} focus and ${compensationText}, so it is worth a closer look.`;
        }
        return `${title} matches your target role and ${compensationText}, with the strongest signal coming from your ranking model.`;
    };
    const summaryText = job.jdSummary || buildMeaningSentence(job, userSignals);

    const handleToggleSavedJob = async (e) => {
        e.stopPropagation();
        if (!user?._id) return;
        if (savedJobEntry?._id) {
            await dispatch(deleteSavedJob(savedJobEntry._id));
        } else {
            await dispatch(saveJob({ userId: user._id, jobId }));
            setSavedToast(true);
            setTimeout(() => setSavedToast(false), 2500);
        }
        await dispatch(fetchSavedJobs(user._id));
    };

    // Category circle config
    const categoryCircles = [
        {
            key: 'compensation',
            label: 'Compensation',
            icon: <DollarSign className="w-4 h-4" />,
            score: normalizedCompanyScores?.compensation,
            category: null,
            tooltip: job.companyId?.scores?.compensation?.bulletPoints
        },
        {
            key: 'WLB',
            label: 'Work-Life Balance',
            icon: <Heart className="w-4 h-4" />,
            score: normalizedCompanyScores?.WLB,
            category: null,
            tooltip: job.companyId?.scores?.workLifeBalance?.bulletPoints
        },
        {
            key: 'growth',
            label: 'Career Growth',
            icon: <TrendingUp className="w-4 h-4" />,
            score: normalizedCompanyScores?.growth,
            category: null,
            tooltip: job.companyId?.scores?.careerGrowth?.bulletPoints
        },
        {
            key: 'toxicity',
            label: 'Toxicity',
            icon: <AlertTriangle className="w-4 h-4" />,
            score: normalizedCompanyScores?.toxicity,
            category: 'toxicity',
            tooltip: job.companyId?.scores?.toxicity?.bulletPoints
        },
        {
            key: 'stability',
            label: 'Stability',
            icon: <Shield className="w-4 h-4" />,
            score: normalizedCompanyScores?.stability,
            category: null,
            tooltip: job.companyId?.scores?.stability?.bulletPoints
        },
        {
            key: 'referral',
            label: 'Referral',
            icon: <Users className="w-4 h-4" />,
            score: normalizedCompanyScores?.referral,
            category: null,
            tooltip: null
        }
    ];

    // Collect all contacts from inline recruiter data
    const allInlineContacts = inlineRecruiterData
        ? [
            ...(inlineRecruiterData.hiring_manager?.best_guesses || []).map(c => ({ ...c, role: 'Hiring Manager' })),
            ...(inlineRecruiterData.recruiters?.best_guesses || []).map(c => ({ ...c, role: 'Recruiter' }))
          ]
        : [];

    const scoreBadgeColor = getScoreBadgeColor(rankedScore);

    return (
        <>
            <div
                ref={cardRef}
                id={job?._id}
                className="w-full bg-white flex flex-col rounded-xl scroll-mt-0 overflow-hidden transition-all duration-200 ease-in-out cursor-pointer border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200"
                onClick={(e) => {
                    // Card click expands the FULL job detail page inline below the card
                    // (no navigation, no back button). Title/View Details still navigate.
                    if (!e.target.closest('input, button, a, [role="button"]')) {
                        toggleExpand();
                    }
                }}
            >
                {/* ── Collapsed header row ── */}
                <div className="px-4 py-3 flex items-center gap-3">
                    {/* Save icon */}
                    <button
                        type="button"
                        onClick={handleToggleSavedJob}
                        className={`shrink-0 p-1 rounded-md transition-colors ${isAlreadySaved ? 'text-[#10B981]' : 'text-gray-300 hover:text-[#3F57E0]'}`}
                        title={isAlreadySaved ? 'Saved' : 'Save job'}
                    >
                        <Bookmark className="w-4 h-4" fill={isAlreadySaved ? 'currentColor' : 'none'} />
                    </button>

                    {/* Company logo */}
                    <img
                        src={getCompanyLogo(job)}
                        alt={getCompanyName(job)}
                        className="rounded-lg border border-slate-200 shrink-0 object-contain bg-white"
                        style={{ width: 42, height: 42 }}
                        onError={(e) => { e.target.src = 'https://cdn-icons-png.flaticon.com/512/2721/2721294.png'; }}
                    />

                    {/* Title + FitPill + meta */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span
                                role="button"
                                tabIndex={0}
                                className="font-semibold text-[15px] text-[#0f172a] leading-tight truncate max-w-[320px] hover:text-[#3F57E0] hover:underline cursor-pointer transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCardClick();
                                    navigate('/jobs/' + job._id, { state: { job } });
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.stopPropagation();
                                        handleCardClick();
                                        navigate('/jobs/' + job._id, { state: { job } });
                                    }
                                }}
                            >
                                {job.jobPosition || job.title || 'Job Position Not Available'}
                            </span>

                            {fitBadgeLabel && (
                                <span className={`inline-flex items-center gap-[5px] px-[9px] py-[2px] rounded-full text-[11.5px] font-medium border shrink-0 ${fitBadgeClass}`}>
                                    {showFitIcon && (
                                        <svg className="w-[9px] h-[9px]" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                            <path d="M5 10L8.5 13.5L15 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                    {fitBadgeLabel}
                                </span>
                            )}

                            {(job.notOnLinkedInYet || (Array.isArray(job.tags) && job.tags.includes('not_on_linkedin_yet'))) && (
                                <span
                                    className="inline-flex items-center gap-1 px-[9px] py-[2px] rounded-full text-[10px] font-bold tracking-wide border shrink-0 bg-orange-50 text-orange-600 border-orange-200"
                                    title="Fetched directly from the company's career page. ATS jobs typically take 24–48 hours to appear on LinkedIn and other job boards — apply before the crowd."
                                >
                                    ⚡ Not on LinkedIn yet
                                </span>
                            )}

                            {isNew && (
                                <span className="inline-flex items-center px-[9px] py-[2px] rounded-full text-[10px] font-bold tracking-wide border shrink-0 bg-emerald-500 text-white border-emerald-500">
                                    NEW
                                </span>
                            )}
                        </div>

                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[13px] text-[#64748b]">
                            <span className="font-medium text-[#334155]">{getCompanyName(job)}</span>
                            <span className="text-[#cbd5e1]">·</span>
                            <span>{job.jobLocation || 'Location not listed'}</span>
                            <span className="text-[#cbd5e1]">·</span>
                            <span>{workType}</span>
                            {(job.jobMinSalary || job.jobMaxSalary) && (
                                <>
                                    <span className="text-[#cbd5e1]">·</span>
                                    <span className="text-[#334155]">
                                        ${job.jobMinSalary?.toLocaleString() || '0'} – ${job.jobMaxSalary?.toLocaleString() || '0'}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Right side: posted date, score badge, View button */}
                    <div className="flex items-center gap-2 shrink-0 ml-auto">
                        <span className="hidden sm:block text-[12px] text-[#94a3b8] whitespace-nowrap">{postedTimeAgo}</span>

                        {rankedScore !== null && (
                            <span
                                className="shrink-0 px-2.5 py-1 rounded-md text-[12.5px] font-bold text-white"
                                style={{ backgroundColor: scoreBadgeColor }}
                            >
                                {rankedScore}<span className="font-normal opacity-75">/100</span>
                            </span>
                        )}

                        <button
                            type="button"
                            className="shrink-0 px-3 py-1.5 rounded-lg text-[13px] font-medium border border-[#3F57E0] text-[#3F57E0] hover:bg-[#3F57E0] hover:text-white transition-colors whitespace-nowrap"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick();
                                navigate('/jobs/' + job._id, { state: { job } });
                            }}
                        >
                            View Details
                        </button>

                        {/* See more — expands the full job detail page inline below */}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
                            className={`shrink-0 inline-flex items-center gap-1 px-3.5 py-1.5 rounded-lg text-[13px] font-medium border shadow-sm transition-all duration-150 whitespace-nowrap ${isExpanded ? 'border-[#3F57E0] bg-[#EEF1FE] text-[#3F57E0]' : 'border-[#E2E8F0] bg-white text-[#475569] hover:border-[#C7D2FE] hover:bg-[#F8FAFF] hover:text-[#3F57E0]'}`}
                        >
                            {isExpanded ? 'See less' : 'See more'}
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* ── Inline full job detail page (embedded, same content as /jobs/:id) ── */}
                {isExpanded && (
                    <div className="border-t border-gray-100 bg-[#F6F7FB]" onClick={(e) => e.stopPropagation()}>
                        <JobDetailPage jobIdProp={job._id} embedded />

                        {/* Sticky collapse bar — stays visible while scrolling the expanded
                            page, with a soft fade so content slides underneath it. */}
                        <div className="sticky bottom-0 z-10 -mt-10 pt-10 pb-3 px-4 flex justify-center bg-gradient-to-t from-[#F6F7FB] via-[#F6F7FB]/85 to-transparent pointer-events-none">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsExpanded(false);
                                    // Bring the collapsed card back into view so the user
                                    // isn't left stranded deep in the list.
                                    requestAnimationFrame(() => {
                                        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                    });
                                }}
                                className="pointer-events-auto inline-flex items-center gap-1.5 pl-4 pr-5 py-2 rounded-full border border-[#C7D2FE] bg-white text-[13px] font-semibold text-[#3F57E0] shadow-[0_4px_14px_rgba(63,87,224,0.18)] hover:bg-[#3F57E0] hover:border-[#3F57E0] hover:text-white active:scale-[0.98] transition-all duration-150"
                            >
                                <ChevronUp className="w-4 h-4" />
                                Collapse job details
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Expanded content (kept for modal usage) ── */}
                {false && (
                    <div className="border-t border-[#e8edff]">
                        {/* Score chip row */}
                        <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-3">
                            <span className="text-[13px] text-[#64748b]">{postedTimeAgo}</span>
                            {rankedScore !== null && (
                                <span
                                    className="px-3 py-1 rounded-md text-[13px] font-bold text-white"
                                    style={{ backgroundColor: scoreBadgeColor }}
                                >
                                    {rankedScore} / 100
                                </span>
                            )}
                        </div>

                        {/* "Why this fits you" */}
                        <div className="px-4 pb-3">
                            <div className="text-[11px] tracking-[0.07em] font-semibold text-[#94a3b8] mb-2">WHY THIS FITS YOU</div>
                            <div className="flex flex-col gap-1.5">
                                {visibleFitReasons.map((reason, index) => {
                                    const isOk = reasonCodeIsOk(reason.code);
                                    return (
                                        <div key={`${job._id || index}-reason-${index}`} className="flex items-start gap-2">
                                            {isOk ? (
                                                <svg className="w-4 h-4 text-[#10B981] shrink-0 mt-[2px]" viewBox="0 0 20 20" fill="none">
                                                    <circle cx="10" cy="10" r="9" stroke="#10B981" strokeWidth="1.5" />
                                                    <path d="M6 10l3 3 5-5" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            ) : (
                                                <svg className="w-4 h-4 text-[#F59E0B] shrink-0 mt-[2px]" viewBox="0 0 20 20" fill="none">
                                                    <circle cx="10" cy="10" r="9" stroke="#F59E0B" strokeWidth="1.5" />
                                                    <path d="M10 6v4M10 13v1" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                            )}
                                            <span className="text-[13.5px] text-[#334155] leading-[1.55]">{reason.text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Category circles */}
                        {showScoresSection && (
                            <div className="px-4 pb-4 border-t border-[#f1f5f9] pt-3">
                                <div className="text-[11px] tracking-[0.07em] font-semibold text-[#94a3b8] mb-3">COMPANY SIGNALS</div>
                                {scoresProcessing ? (
                                    <div className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 px-4 py-3 text-sm text-indigo-700 animate-pulse">
                                        <svg className="w-4 h-4 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16 8 8 0 01-8-8z"/>
                                        </svg>
                                        <span className="font-medium">AI performing deep company analysis…</span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                        {categoryCircles.map((circle) => {
                                            const colors = getCircleColors(circle.score, circle.category);
                                            const scoreVal = circle.score != null && Number.isFinite(Number(circle.score))
                                                ? Math.round(Number(circle.score))
                                                : null;
                                            return (
                                                <Tooltip
                                                    key={circle.key}
                                                    title={
                                                        circle.tooltip?.length > 0 ? (
                                                            <div className="max-w-xs text-left">
                                                                <div className="font-bold mb-2 text-white text-sm">{circle.label}</div>
                                                                <ul className="list-disc pl-4 m-0 space-y-1">
                                                                    {circle.tooltip.map((pt, i) => (
                                                                        <li key={i} className="text-xs">{pt}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        ) : circle.label
                                                    }
                                                    placement="top"
                                                    arrow
                                                >
                                                    <div className="flex flex-col items-center gap-1 cursor-pointer">
                                                        <div
                                                            className="flex items-center justify-center rounded-full"
                                                            style={{
                                                                width: 42,
                                                                height: 42,
                                                                backgroundColor: colors.bg,
                                                                border: `1.5px solid ${colors.border}`,
                                                                color: colors.text
                                                            }}
                                                        >
                                                            {circle.icon}
                                                        </div>
                                                        <span className="text-[10.5px] text-[#64748b] text-center leading-tight">{circle.label}</span>
                                                        <span className="text-[11px] font-semibold" style={{ color: colors.text }}>
                                                            {scoreVal !== null
                                                                ? <ScoreDisplay status={normalizedScoresStatus} score={circle.score} category={circle.category} />
                                                                : <span className="text-[#94a3b8]">—</span>
                                                            }
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* AI Summary */}
                        {summaryText && (
                            <div className="px-4 pb-4 border-t border-[#f1f5f9] pt-3">
                                <div className="text-[11px] tracking-[0.07em] font-semibold text-[#94a3b8] mb-2">AI SUMMARY</div>
                                <p className="text-[13.5px] text-[#334155] leading-[1.65]">{summaryText}</p>
                            </div>
                        )}

                        {/* Score breakdown */}
                        {scoreBreakdown && (() => {
                            const breakdownWeights = scoreBreakdown?.weights || {};
                            const titleMax = Number.isFinite(Number(breakdownWeights.titleMatch)) ? Number(breakdownWeights.titleMatch) : 25;
                            const compensationMax = Number.isFinite(Number(breakdownWeights.compensation)) ? Number(breakdownWeights.compensation) : 20;
                            const competitionMax = Number.isFinite(Number(breakdownWeights.competition)) ? Number(breakdownWeights.competition) : 25;
                            const recencyMax = Number.isFinite(Number(breakdownWeights.recency)) ? Number(breakdownWeights.recency) : 15;
                            const industryMax = Number.isFinite(Number(breakdownWeights.industryFit)) ? Number(breakdownWeights.industryFit) : 15;
                            const educationMax = Number.isFinite(Number(breakdownWeights.educationMatch)) ? Number(breakdownWeights.educationMatch) : 10;
                            const experienceMax = Number.isFinite(Number(breakdownWeights.experienceMatch)) ? Number(breakdownWeights.experienceMatch) : 10;
                            const breakdownRows = [
                                { label: 'Title match', max: titleMax, val: Math.round(scoreBreakdown.titleMatch || scoreBreakdown.title || 0) },
                                { label: 'Compensation', max: compensationMax, val: Math.round(scoreBreakdown.compensation || 0) },
                                { label: 'Competition', max: competitionMax, val: Math.round(scoreBreakdown.competition || 0) },
                                { label: 'Recency', max: recencyMax, val: Math.round(scoreBreakdown.recency || 0) },
                                { label: 'Industry fit', max: industryMax, val: Math.round(scoreBreakdown.industryFit || scoreBreakdown.industry || 0) },
                                { label: 'Education fit', max: educationMax, val: Math.round(scoreBreakdown.educationMatch || 0) },
                                { label: 'Experience fit', max: experienceMax, val: Math.round(scoreBreakdown.experienceMatch || 0) }
                            ];
                            return (
                                <div className="px-4 pb-4 border-t border-[#f1f5f9] pt-3">
                                    <div className="text-[11px] tracking-[0.07em] font-semibold text-[#94a3b8] mb-2">MATCH BREAKDOWN</div>
                                    {breakdownRows.map((row) => (
                                        <div key={row.label} className="flex items-center gap-[10px] mb-[6px]">
                                            <span className="w-[112px] shrink-0 text-[12.5px] text-[#64748b]">{row.label}</span>
                                            <div className="flex-1 h-[4px] bg-[#f1f5f9] rounded-full overflow-hidden">
                                                <div className="h-full rounded-full bg-[#3F57E0]" style={{ width: `${Math.max(0, Math.min(100, (row.val / row.max) * 100))}%` }} />
                                            </div>
                                            <span className="w-[32px] shrink-0 text-right text-[12px] font-medium text-[#334155]">{Math.max(0, row.val)}</span>
                                        </div>
                                    ))}
                                    <div className="flex items-center gap-[10px] mt-2 pt-2 border-t border-[#f1f5f9]">
                                        <span className="text-[12.5px] text-[#64748b]">Total</span>
                                        <div className="flex-1 h-[4px] bg-[#f1f5f9] rounded-full overflow-hidden">
                                            <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, rankedScore || 0))}%`, backgroundColor: scoreAccent }} />
                                        </div>
                                        <span className="text-[13px] font-semibold" style={{ color: scoreAccent }}>{`${Math.max(0, rankedScore || 0)} / 100`}</span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Show Referral Path section */}
                        <div className="px-4 pb-4 border-t border-[#f1f5f9] pt-3">
                            {!referralPathRequested ? (
                                <button
                                    type="button"
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3F57E0] text-white text-[13.5px] font-medium hover:bg-[#3347c8] transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleFindRecruiter();
                                    }}
                                >
                                    <Users className="w-4 h-4" />
                                    Show Referral Path
                                </button>
                            ) : inlineRecruiterLoading ? (
                                <div className="flex items-center gap-3 py-3">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#3F57E0] shrink-0" />
                                    <span className="text-[13.5px] text-[#64748b]">Finding recruiters at {getCompanyName(job)}…</span>
                                </div>
                            ) : inlineRecruiterData && inlineRecruiterData.fetchFailed ? (
                                <div className="flex items-center gap-2 py-3 text-[13.5px] text-[#64748b]">
                                    <svg className="w-4 h-4 text-amber-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    No verified contacts found for this company yet.
                                </div>
                            ) : allInlineContacts.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                    <div className="text-[11px] tracking-[0.07em] font-semibold text-[#94a3b8] mb-1">REFERRAL PATH</div>
                                    {allInlineContacts.slice(0, 3).map((contact, idx) => (
                                        <div key={idx} className="flex items-center gap-3 p-3 rounded-xl border border-[#e8edff] bg-[#fafbff]">
                                            <div className="w-9 h-9 rounded-full bg-[#e8edff] flex items-center justify-center shrink-0 text-[#3F57E0] font-bold text-[14px]">
                                                {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13.5px] font-semibold text-[#0f172a] truncate">{contact.name || 'Unknown'}</div>
                                                <div className="text-[12px] text-[#64748b] truncate">{contact.title || contact.role || ''}</div>
                                                {contact.email && (
                                                    <div className="text-[11.5px] text-[#3F57E0] truncate mt-0.5">{contact.email}</div>
                                                )}
                                            </div>
                                            {contact.referral_probability != null && (
                                                <div className="shrink-0 text-right">
                                                    <div className="text-[11px] text-[#94a3b8]">Referral</div>
                                                    <div className="text-[13px] font-semibold text-[#10B981]">
                                                        {Math.round(Number(contact.referral_probability) * 100)}%
                                                    </div>
                                                </div>
                                            )}
                                            {contact.profile_url && (
                                                <a
                                                    href={contact.profile_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="shrink-0 p-1.5 rounded-lg border border-[#bfdbfe] text-[#3F57E0] hover:bg-[#eff6ff] transition-colors"
                                                    title="LinkedIn"
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5" />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                    {allInlineContacts.length > 3 && (
                                        <span className="text-[12px] text-[#3F57E0]">+{allInlineContacts.length - 3} more in full strategy</span>
                                    )}
                                </div>
                            ) : null}
                        </div>

                        {/* Footer row */}
                        <div className="px-4 py-3 border-t border-[#f1f5f9] flex items-center gap-2">
                            {/* Bookmark */}
                            <button
                                type="button"
                                aria-label="Save job"
                                className="w-8 h-8 rounded-lg border border-[#e2e8f0] bg-white flex items-center justify-center text-[#334155] hover:border-[#3F57E0] hover:bg-[#eff6ff] transition-colors"
                                onClick={handleToggleSavedJob}
                            >
                                <Bookmark className="w-[14px] h-[14px]" fill={isAlreadySaved ? '#3F57E0' : 'none'} stroke={isAlreadySaved ? '#3F57E0' : 'currentColor'} />
                            </button>

                            {/* Share */}
                            <button
                                type="button"
                                aria-label="Share job"
                                className="w-8 h-8 rounded-lg border border-[#e2e8f0] bg-white flex items-center justify-center text-[#334155] hover:border-[#3F57E0] hover:bg-[#eff6ff] transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const url = job.applyLink || window.location.href;
                                    if (navigator.share) {
                                        navigator.share({ title: job.jobPosition || 'Job', url });
                                    } else {
                                        navigator.clipboard?.writeText(url);
                                        setToastMessage({ show: true, message: 'Link copied!', type: 'success' });
                                    }
                                }}
                            >
                                <Share2 className="w-[14px] h-[14px]" />
                            </button>

                            <div className="flex-1" />

                            {/* Open full strategy */}
                            {inlineRecruiterData && !inlineRecruiterData.fetchFailed && allInlineContacts.length > 0 && (
                                <button
                                    type="button"
                                    className="px-3 py-1.5 rounded-lg border border-[#3F57E0] text-[#3F57E0] text-[12.5px] font-medium hover:bg-[#3F57E0] hover:text-white transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        ensureFullJob();
                                        setRecruiterInfo(inlineRecruiterData);
                                        setIsRecruiterModalOpen(true);
                                    }}
                                >
                                    Open full strategy
                                </button>
                            )}

                            {/* Full description */}
                            <button
                                type="button"
                                className="px-3 py-1.5 rounded-lg border border-[#e2e8f0] bg-white text-[#334155] text-[12.5px] font-medium hover:bg-[#f8fafc] transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    ensureFullJob();
                                    setIsJobDetailsModalOpen(true);
                                }}
                            >
                                Full description
                            </button>

                            {/* Collapse */}
                            <button
                                type="button"
                                aria-expanded={isExpanded}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#e2e8f0] bg-white text-[12.5px] text-[#334155] hover:bg-[#f8fafc] transition-colors whitespace-nowrap"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand();
                                }}
                            >
                                Collapse
                                <ChevronUp className="w-3 h-3" />
                            </button>

                            {/* Apply */}
                            {job.applyLink && (
                                <a
                                    href={job.applyLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="px-4 py-1.5 rounded-lg bg-[#3F57E0] text-white text-[12.5px] font-medium hover:bg-[#3347c8] transition-colors"
                                >
                                    Apply
                                </a>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {isRecruiterModalOpen && (
                <RecruiterModal
                    data={recruiterInfo}
                    onClose={handleCloseRecruiterModal}
                    job={detailJob || job}
                    handleSaveSelectedJobs={handleSaveSelectedJobs}
                />
            )}

            {isJobDetailsModalOpen && (
                <JobDetailsModal
                    isOpen={isJobDetailsModalOpen}
                    onClose={() => setIsJobDetailsModalOpen(false)}
                    job={detailJob || job}
                    loading={detailLoading}
                />
            )}

            {savedToast && (
                <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 bg-[#3F57E0] text-white px-4 py-2 rounded-lg shadow-lg font-dmsans text-sm font-medium flex items-center gap-2 pointer-events-none">
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Job saved!
                </div>
            )}
        </>
    );
};

export default JobCard;
